import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    mode: 'light',
    background: {
      default: '#f8fffe', // Light teal-tinted white
      paper: '#ffffff',
    },
    primary: {
      main: '#00796b', // Teal color for primary (user specified)
      light: '#4db6ac',
      dark: '#004d40',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#004d40', // Darker teal for secondary (user specified)
      light: '#39796b',
      dark: '#00251a',
      contrastText: '#ffffff',
    },
    text: {
      primary: '#1a2f2c',
      secondary: '#4a6560',
    },
    success: {
      main: '#2e7d32',
      light: '#4caf50',
    },
    warning: {
      main: '#f9a825',
      light: '#ffd54f',
    },
    error: {
      main: '#c62828',
      light: '#ef5350',
    },
    info: {
      main: '#0288d1',
      light: '#4fc3f7',
    },
    grey: {
      50: '#f5f9f8',
      100: '#e8f0ef',
      200: '#d0e2e0',
      300: '#b0ccc9',
      400: '#8ab3af',
      500: '#6a9994',
      600: '#507f7a',
      700: '#3d6661',
      800: '#2d4d49',
      900: '#1d3331',
    },
  },
  typography: {
    fontFamily: `'DM Sans', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`,
    h1: {
      fontWeight: 800,
      fontSize: '3.5rem',
      letterSpacing: '-0.02em',
      lineHeight: 1.1,
    },
    h2: {
      fontWeight: 700,
      fontSize: '2.75rem',
      letterSpacing: '-0.01em',
      lineHeight: 1.2,
    },
    h3: {
      fontWeight: 700,
      fontSize: '2.25rem',
      letterSpacing: '-0.01em',
      lineHeight: 1.25,
    },
    h4: {
      fontWeight: 700,
      fontSize: '1.75rem',
      letterSpacing: '-0.01em',
      lineHeight: 1.3,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.35rem',
      lineHeight: 1.4,
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.1rem',
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.7,
    },
    body2: {
      fontSize: '0.9rem',
      lineHeight: 1.6,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      letterSpacing: '0.01em',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          scrollBehavior: 'smooth',
        },
        body: {
          overflowX: 'hidden',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          padding: '12px 28px',
          fontSize: '1rem',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '-100%',
            width: '100%',
            height: '100%',
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
            transition: 'left 0.5s ease',
          },
          '&:hover::before': {
            left: '100%',
          },
          '&:hover': {
            transform: 'translateY(-3px)',
            boxShadow: '0 12px 28px -8px rgba(0, 121, 107, 0.35)',
          },
          '&:active': {
            transform: 'translateY(-1px)',
          },
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #00796b 0%, #004d40 100%)',
          boxShadow: '0 4px 16px -4px rgba(0, 121, 107, 0.4)',
          '&:hover': {
            background: 'linear-gradient(135deg, #00897b 0%, #00695c 100%)',
            boxShadow: '0 12px 28px -8px rgba(0, 121, 107, 0.5)',
          },
        },
        containedSecondary: {
          background: 'linear-gradient(135deg, #004d40 0%, #00251a 100%)',
          boxShadow: '0 4px 16px -4px rgba(0, 77, 64, 0.4)',
          '&:hover': {
            background: 'linear-gradient(135deg, #00695c 0%, #003d33 100%)',
            boxShadow: '0 12px 28px -8px rgba(0, 77, 64, 0.5)',
          },
        },
        outlined: {
          borderWidth: '2px',
          '&:hover': {
            borderWidth: '2px',
            background: 'rgba(0, 121, 107, 0.08)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '20px',
          boxShadow: '0 4px 24px -4px rgba(0, 77, 64, 0.08)',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          border: '1px solid rgba(0, 121, 107, 0.08)',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: '0 20px 40px -12px rgba(0, 121, 107, 0.2)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        },
        elevation1: {
          boxShadow: '0 2px 12px -2px rgba(0, 77, 64, 0.08)',
        },
        elevation4: {
          boxShadow: '0 8px 24px -4px rgba(0, 77, 64, 0.12)',
        },
        elevation6: {
          boxShadow: '0 12px 32px -8px rgba(0, 77, 64, 0.15)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 4px 12px -2px rgba(0, 121, 107, 0.15)',
            },
            '&.Mui-focused': {
              boxShadow: '0 4px 16px -2px rgba(0, 121, 107, 0.2)',
            },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(20px) saturate(180%)',
          boxShadow: '0 1px 20px 0 rgba(0, 77, 64, 0.08)',
          borderBottom: '1px solid rgba(0, 121, 107, 0.08)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '10px',
          fontWeight: 500,
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          border: '3px solid rgba(0, 121, 107, 0.2)',
        },
      },
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0 2px 8px -2px rgba(0, 77, 64, 0.08)',
    '0 4px 12px -2px rgba(0, 77, 64, 0.1)',
    '0 6px 16px -4px rgba(0, 77, 64, 0.12)',
    '0 8px 20px -4px rgba(0, 77, 64, 0.14)',
    '0 10px 24px -6px rgba(0, 77, 64, 0.16)',
    '0 12px 28px -8px rgba(0, 77, 64, 0.18)',
    '0 14px 32px -8px rgba(0, 77, 64, 0.2)',
    '0 16px 36px -10px rgba(0, 77, 64, 0.22)',
    '0 18px 40px -10px rgba(0, 77, 64, 0.24)',
    '0 20px 44px -12px rgba(0, 77, 64, 0.26)',
    '0 22px 48px -12px rgba(0, 77, 64, 0.28)',
    '0 24px 52px -14px rgba(0, 77, 64, 0.3)',
    ...Array(12).fill('0 24px 52px -14px rgba(0, 77, 64, 0.3)'),
  ],
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
});

export default theme;
